import { Spinner } from "@pervasivemind/bi-components";
import { X } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { GenericChart } from "./GenericChart";
import { KPICard } from "./KPICard";
import { formatCellValue } from "./formatting";
import { resolveLocalized, type DisplayLabel } from "./i18n-resolve";
import { useLang } from "./i18n-binding";
import type { ChartConfig, ChartData, DrillData, Kpi } from "./types";

export interface DrillSectionProps {
  /** When non-null, the section renders. Pass null to hide entirely. */
  drillId: string | null;
  /** Header title (already locale-resolved by the caller; may also be a
   *  raw DisplayLabel — resolved defensively here). */
  title?: DisplayLabel | null;
  description?: DisplayLabel | null;
  /** Drill payload returned by the app's ``fetchDrill``. */
  data: DrillData | null;
  loading: boolean;
  error?: string | null;
  /** Chart config lookup (colors, formats, table_columns) by chart title. */
  chartConfigs?: ChartConfig[];
  onClose: () => void;
}

/**
 * Inline drill-down section that opens below the KPI strip when a
 * drill-targeted KPI is clicked. Single shared copy (was duplicated in
 * builder + portal — the builder's mirror drifted and crashed with React #31
 * on bilingual labels; this resolves every ``{en,es}`` label/title via
 * ``resolveLocalized`` so the crash can't recur in either app).
 */
export function DrillSection({
  drillId,
  title,
  description,
  data,
  loading,
  error,
  chartConfigs,
  onClose,
}: DrillSectionProps): JSX.Element | null {
  const { t } = useTranslation();
  const lang = useLang();
  if (!drillId) return null;

  const heading = resolveLocalized(title as DisplayLabel, lang) || t("drill.title");
  const resolvedDescription = description
    ? resolveLocalized(description as DisplayLabel, lang)
    : "";

  let body: ReactNode;
  if (loading) {
    body = (
      <div className="flex h-48 items-center justify-center">
        {/* Standard action-level loader (§2.3) — 20px accent Spinner
            replaces the old grey Loader2. */}
        <Spinner size={20} ariaLabel={t("common.loadingData")} />
      </div>
    );
  } else if (error) {
    body = (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-negative">
        {error}
      </div>
    );
  } else if (!data) {
    body = <div className="text-sm text-neutral-500">{t("drill.noData")}</div>;
  } else {
    body = (
      <div className="space-y-4">
        {data.kpis.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {data.kpis.map((kpi: Kpi) => {
              // kpi.label may be bilingual `{en, es}`. Resolve once for both
              // the key + title; the resolved string is stable per-locale so
              // the React key holds across renders.
              const resolvedLabel = resolveLocalized(kpi.label as DisplayLabel, lang);
              return (
                <KPICard
                  key={resolvedLabel}
                  title={resolvedLabel}
                  value={formatCellValue(kpi.value, kpi.format)}
                  variant={(kpi.variant ?? "card") as never}
                  max={kpi.max ?? undefined}
                  gradient={kpi.gradient ?? undefined}
                  icon={kpi.icon ?? undefined}
                  iconBg={kpi.icon_bg ?? undefined}
                  sparkData={kpi.spark_data ?? undefined}
                  previous={
                    kpi.previous == null
                      ? undefined
                      : kpi.format === "percent"
                        ? kpi.previous * 100
                        : kpi.previous
                  }
                  numericValue={
                    typeof kpi.value === "number"
                      ? kpi.format === "percent"
                        ? kpi.value * 100
                        : kpi.value
                      : undefined
                  }
                  format={(kpi.format ?? undefined) as never}
                  severity={kpi.severity ?? undefined}
                  description={
                    kpi.description
                      ? resolveLocalized(kpi.description as DisplayLabel, lang)
                      : undefined
                  }
                  expected={kpi.expected ?? undefined}
                  breakdown={kpi.breakdown ?? undefined}
                />
              );
            })}
          </div>
        )}
        {data.charts.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {data.charts.map((chart: ChartData) => {
              const cfg =
                chartConfigs?.find(
                  (c) =>
                    resolveLocalized(c.title as DisplayLabel, lang) ===
                    resolveLocalized(chart.title as DisplayLabel, lang),
                ) ?? null;
              // chart.title may be bilingual.
              const resolvedTitle = resolveLocalized(chart.title as DisplayLabel, lang);
              return (
                <GenericChart
                  key={resolvedTitle}
                  type={chart.type}
                  title={resolvedTitle}
                  data={chart.data}
                  error={chart.error}
                  tone={cfg?.tone ?? null}
                  tones={cfg?.tones ?? []}
                  stacked={cfg?.stacked ?? false}
                  xFormat={(cfg?.x_format ?? null) as never}
                  yFormat={(cfg?.y_format ?? null) as never}
                  dataLabels={(cfg?.data_labels ?? null) as never}
                  axisXFormat={(cfg?.axis_format?.x ?? null) as never}
                  axisYFormat={(cfg?.axis_format?.y ?? null) as never}
                  axisY1Format={(cfg?.axis_format?.y1 ?? null) as never}
                  dualAxis={(cfg?.dual_axis ?? false) as never}
                  tableColumns={(cfg?.table_columns ?? []) as never}
                  mapVariant={(cfg?.map_variant ?? undefined) as never}
                  calendarYear={(cfg?.year ?? null) as never}
                  timelineMonths={(cfg?.months_csv ?? null) as never}
                />
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-accent/25 bg-white shadow-sm overflow-hidden">
      <div className="flex items-start justify-between gap-3 border-b border-neutral-200 bg-accent/[0.04] px-4 py-3">
        <div>
          <div className="text-2xs font-medium uppercase tracking-wide text-accent-dark">
            {t("drill.fromKpi")}
          </div>
          <h3 className="text-sm font-semibold text-neutral-900">{heading}</h3>
          {resolvedDescription && (
            <p className="mt-1 text-xs text-neutral-500">{resolvedDescription}</p>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 inline-flex items-center gap-1 rounded-md border border-neutral-300 bg-white px-2 py-1 text-xs text-neutral-700 hover:bg-neutral-100"
          aria-label={t("drill.closeAnalysis")}
        >
          <X size={12} /> {t("drill.close")}
        </button>
      </div>
      <div className="p-4">{body}</div>
    </div>
  );
}
